import { inject, Injectable } from '@angular/core';

import { ComponentStore } from '@ngrx/component-store';
import { Observable } from 'rxjs';
import { filter, switchMapTo, tap, withLatestFrom } from 'rxjs/operators';

import { AuthFacade } from '@fantaz/shared/auth/auth-facade';
import { FzGoogleTagManagerService } from '@fantaz/shared/data-access/api';
import { GAEventKeys } from '@fantaz/shared/data-access/models';

enum ConnectionStatus {
  ONLINE,
  OFFLINE,
}

enum ThemeBrightness {
  LIGHT,
  DARK,
}

export enum ColorScheme {
  Dark = 'dark',
  Light = 'light',
}

interface UIState {
  /**
   * Connection status of the UI.
   */
  connectionStatus?: ConnectionStatus;

  /**
   * Language of the UI.
   */
  language?: string;

  /**
   * Timezone of the UI.
   */
  timezone: string;

  /**
   * Default user preferences
   */
  userPreferences: {
    pagination: {
      pageSize: number;
    };
  };

  /**
   * Theme config
   */
  themeConfig: {
    brightness: ThemeBrightness;
  };

  navItems: any[];
  isShowUnauthorizedModal: boolean;
  isShowShareBottomSheet: boolean;
}

@Injectable({ providedIn: 'root' })
export class UIStore extends ComponentStore<UIState> {
  googleTagManager = inject(FzGoogleTagManagerService);
  auth = inject(AuthFacade);

  /**
   * Get default timezone from browser using Intl.DateTimeFormat
   */
  static browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  static localPageSize = Number(
    window.localStorage.getItem('pageSize') as string,
  );

  constructor() {
    super({
      timezone: UIStore.browserTimezone,
      // set by default en-US language
      language: 'en-US',
      themeConfig: {
        brightness: ThemeBrightness.LIGHT,
      },
      navItems: [
        { label: 'Home', path: '', exact: true },
        { label: 'Play', path: '/tournaments' },
        { label: 'Promote', path: '/promote' },
        { label: 'Profit', path: '/profit' },
      ],
      userPreferences: {
        pagination: {
          pageSize: UIStore.localPageSize,
        },
      },
      isShowUnauthorizedModal: false,
      isShowShareBottomSheet: false,
    });
  }

  readonly isShowUnauthorizedModal$ = this.select(
    (s) => s.isShowUnauthorizedModal,
  );
  readonly isShowShareBottomSheet$ = this.select(
    (s) => s.isShowShareBottomSheet,
  );
  readonly navItems$ = this.select(({ navItems }) => navItems);
  readonly timezone$ = this.select((s) => s.timezone);

  readonly userPreferences$ = this.select((s) => s.userPreferences);

  readonly showUnauthorizedModal = this.effect((params$) =>
    params$.pipe(
      switchMapTo(this.isShowUnauthorizedModal$),
      filter((s) => !s),
      tap(() => {
        this.patchState({
          isShowUnauthorizedModal: true,
        });
        alert('Your access token has expired!');
      }),
    ),
  );

  // TODO: Move share service into this
  readonly shareUrl = this.effect((params$) =>
    params$.pipe(
      switchMapTo(this.isShowShareBottomSheet$),
      filter((s) => !s),
      tap(() => {
        this.patchState({
          isShowShareBottomSheet: true,
        });
        alert('Your access token has expired!');
      }),
    ),
  );

  /**
   * Report data to Google Analytics
   *
   * @param {object} data
   * @param {string} data.event
   */
  readonly reportToGoogleAnalytics = this.effect(
    (
      data$: Observable<
        {
          event: GAEventKeys;
        } & any
      >,
    ) =>
      data$.pipe(
        withLatestFrom(this.auth.currentUser$),
        tap(([data, currentUser]) => {
          // prepare event to send of google analytics
          let event = {
            ...data,
            timestamp: Date.now(),
          };

          // add auth user info to event object in case of exist
          if (currentUser) {
            event = {
              ...event,
              user_name: currentUser.user.username,
              user_id: currentUser.user.uuid,
            };
          }

          console.log('Google Analytics', event);

          // send event
          this.googleTagManager.pushTag(event);
        }),
      ),
  );

  setTimezone(timezone?: string) {
    if (timezone && timezone !== undefined && timezone !== '') {
      this.patchState({
        timezone,
      });
    }
  }
  setPageSize(pageSize?: number) {
    if (pageSize !== null && pageSize !== undefined && pageSize >= 0) {
      window.localStorage.setItem('pageSize', pageSize.toString());
      this.patchState({
        userPreferences: {
          pagination: {
            pageSize,
          },
        },
      });
    }
  }
}
